import { api, onUnauthorized, setCsrfToken, setReauthenticator, setWorkspaceId } from '../api/client';
import type { AppConfig } from '../api/types';
import { rememberReturnPath } from '../auth/return-path';
import type { SupabaseAuth } from '../auth/supabase';
import { PIN_KEY, clearPin } from '../auth/workspace-pin';

/** Shell-less routes; must agree with `publicRoutes` in routes.tsx (a routes test checks it). */
export function isPublicPath(pathname: string): boolean {
  return (
    pathname === '/login' ||
    pathname === '/auth/callback' ||
    pathname === '/oauth/consent' ||
    pathname === '/account-deleted' ||
    ['/p/', '/print/', '/card/'].some((prefix) => pathname.startsWith(prefix))
  );
}

export type SessionStatus = 'unknown' | 'signed-in' | 'signed-out';

export interface SessionSnapshot {
  status: SessionStatus;
  config: AppConfig | null;
  configLoaded: boolean;
  mode: 'accounts' | 'admin';
  workspaceId: string | null;
  supabase: SupabaseAuth | null;
  supabaseFailed: boolean;
  signInNotice: string | null;
  /** The signed-in person's `principal.label` (email in accounts mode, the admin label in admin mode); null until known and after sign-out. */
  userLabel: string | null;
}

/** The workspace in the URL is not one this person may open. */
export class NotAMemberError extends Error {
  constructor(workspaceId: string) {
    super(`Not a member of workspace ${workspaceId}`);
    this.name = 'NotAMemberError';
  }
}

export interface SessionStore {
  getSnapshot(): SessionSnapshot;
  subscribe(listener: () => void): () => void;
  /** Loads config, probes `GET /api/auth/me`, starts Supabase in accounts mode. Idempotent. Read state with getSnapshot(). */
  ready(): Promise<void>;
  /**
   * No-op when `workspaceId` is already current, no switch is in flight and (accounts) the last switch landed on it. Accounts: verifies membership (NotAMemberError),
   * awaits `api.switchWorkspace(id)`, then pins the id and sets the request header; if the switch
   * request rejects, rethrows with pin, header and snapshot unchanged. Admin: requires the
   * principal's id (NotAMemberError otherwise) and makes no switch request.
   */
  enterWorkspace(workspaceId: string): Promise<void>;
  /** The id `/` and old links redirect to: a member ?w= value, then the pin, then the principal's. */
  defaultWorkspace(search: string): Promise<string>;
  signOut(): Promise<void>;
  markSignedIn(): void;
  /** After a workspace delete: drop the pin if it names `workspaceId` and the cached principal workspace, so `defaultWorkspace` asks `api.me()` again. */
  forgetDeletedWorkspace(workspaceId: string): void;
  setSignInNotice(message: string | null): void;
}

/**
 * The session lifecycle, outside React, so the router's `beforeLoad` and the
 * components read one source.
 *
 * The URL owns the workspace: a route calls `enterWorkspace(id)` and the store
 * only moves the pin, the request header and the snapshot once the server has
 * accepted the switch. Navigation (to `/login`, to the return path) is the
 * caller's job; this module knows no routes beyond `isPublicPath`.
 */
export function createSessionStore(deps: {
  storage: Storage;
  loadSupabase: (config: { supabaseUrl: string; supabaseAnonKey: string }, origin: string) => Promise<SupabaseAuth>;
  origin: string;
}): SessionStore {
  const { storage } = deps;
  let snapshot: SessionSnapshot = {
    status: 'unknown',
    config: null,
    configLoaded: false,
    mode: 'admin',
    workspaceId: null,
    supabase: null,
    supabaseFailed: false,
    signInNotice: null,
    userLabel: null
  };
  const listeners = new Set<() => void>();

  function update(patch: Partial<SessionSnapshot>): void {
    const changed = (Object.keys(patch) as Array<keyof SessionSnapshot>).some((key) => snapshot[key] !== patch[key]);
    if (!changed) return;
    snapshot = { ...snapshot, ...patch };
    for (const listener of listeners) listener();
  }

  let readyPromise: Promise<void> | null = null;
  let supabaseLoad: Promise<SupabaseAuth> | null = null;
  /** The principal's own workspace, from `GET /api/auth/me`; null until known. */
  let principalWorkspace: string | null = null;
  // Set the moment the person signs out, cleared when they sign in again: a
  // late 401 in between must never quietly re-exchange them back in.
  let signedOut = false;
  let signingOut: Promise<void> | null = null;
  /** Bumped by every entry and by every loss of session: only the newest entry may apply. */
  let enterSeq = 0;
  let latestEnter: { id: string; promise: Promise<void> } | null = null;
  // Switch requests run in order, so the server ends on the last one chosen.
  let switchChain: Promise<unknown> = Promise.resolve();
  // Switch requests queued and not yet settled. An entry that failed before
  // switching clears `latestEnter` while an older switch may still be running.
  let pendingSwitches = 0;
  // The workspace the last switch this store made and saw resolve put the
  // server on; null until one has. Never seeded from `api.me()` and never used
  // to skip a switch the store has not made itself: it can only make the
  // current-workspace no-op rarer (more requests), never skip one.
  let serverWorkspace: string | null = null;

  const accounts = (): boolean => snapshot.mode === 'accounts';

  function forgetWorkspace(): void {
    enterSeq++;
    latestEnter = null;
    principalWorkspace = null;
    serverWorkspace = null;
    setWorkspaceId(null);
    update({ workspaceId: null, userLabel: null });
  }

  function handleUnauthorized(): void {
    setCsrfToken(null);
    if (accounts() && !isPublicPath(window.location.pathname)) rememberReturnPath();
    forgetWorkspace();
    update({ status: 'signed-out' });
  }

  // A 401 with a live Supabase session re-exchanges and retries; it must
  // resolve false, never reject, on any failure.
  async function reauthenticate(): Promise<boolean> {
    if (signedOut) return false;
    try {
      const auth = snapshot.supabase;
      if (!auth) return false;
      const token = await auth.getAccessToken();
      if (!token || signedOut) return false;
      const result = await api.exchange(token);
      principalWorkspace = result.principal.workspaceId;
      update({ userLabel: result.principal.label });
      // Unregistered while revalidating: a 401 here must not wait on this
      // very reauthentication (it would deadlock and poison later 401s).
      setReauthenticator(null);
      try {
        const current = snapshot.workspaceId;
        if (current !== null) {
          const ids = new Set((await api.listWorkspaces()).workspaces.map((w) => w.id));
          // Never fall through to another workspace's data: the route errors instead.
          if (!ids.has(current)) return false;
        }
      } finally {
        if (!signedOut) setReauthenticator(reauthenticate);
      }
      return !signedOut;
    } catch {
      return false;
    }
  }

  // One Supabase client per app: loading it again would make supabase-js warn
  // about multiple GoTrueClient instances.
  function startSupabase(config: AppConfig): void {
    if (supabaseLoad) return;
    if (!config.supabaseUrl || !config.supabaseAnonKey) {
      update({ supabaseFailed: true });
      return;
    }
    supabaseLoad = deps.loadSupabase(
      { supabaseUrl: config.supabaseUrl, supabaseAnonKey: config.supabaseAnonKey },
      deps.origin
    );
    supabaseLoad.then(
      (auth) => {
        update({ supabase: auth });
        setReauthenticator(reauthenticate);
      },
      () => update({ supabaseFailed: true })
    );
  }

  async function principal(): Promise<string> {
    if (principalWorkspace === null) {
      const { principal: who } = await api.me();
      principalWorkspace = who.workspaceId;
      update({ userLabel: who.label });
    }
    return principalWorkspace;
  }

  async function memberIds(): Promise<Set<string>> {
    return new Set((await api.listWorkspaces()).workspaces.map((w) => w.id));
  }

  function switchServer(id: string): Promise<void> {
    pendingSwitches++;
    const request = switchChain
      .then(() => api.switchWorkspace(id))
      .then(() => {
        serverWorkspace = id;
      })
      .finally(() => {
        pendingSwitches--;
      });
    switchChain = request.catch(() => {});
    return request;
  }

  async function enter(id: string, seq: number): Promise<void> {
    await store.ready();
    if (!accounts()) {
      if (id !== (await principal())) throw new NotAMemberError(id);
      if (seq === enterSeq) update({ workspaceId: id });
      return;
    }
    if (!(await memberIds()).has(id)) throw new NotAMemberError(id);
    if (seq !== enterSeq) return;
    await switchServer(id);
    // A later entry (or a sign-out) superseded this one while the switch ran.
    if (seq !== enterSeq) return;
    storage.setItem(PIN_KEY, id);
    setWorkspaceId(id);
    update({ workspaceId: id });
  }

  const store: SessionStore = {
    getSnapshot: () => snapshot,

    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },

    ready() {
      readyPromise ??= (async () => {
        let config: AppConfig | null = null;
        try {
          config = await api.config();
        } catch {
          // Not fatal: a missing config costs the MCP endpoint on the connect
          // page and the development-only seeding hint, nothing else.
        }
        update({ config, configLoaded: true, mode: config?.auth === 'accounts' ? 'accounts' : 'admin' });
        onUnauthorized(handleUnauthorized);
        if (config?.auth === 'accounts') startSupabase(config);
        try {
          await principal();
          update({ status: 'signed-in' });
        } catch {
          update({ status: 'signed-out' });
        }
      })();
      return readyPromise;
    },

    enterWorkspace(id) {
      if (latestEnter && latestEnter.id === id) return latestEnter.promise;
      const seq = ++enterSeq;
      // Only a no-op when no switch is in flight and the last one this store
      // made landed on this workspace; otherwise the server may be elsewhere.
      // Admin mode never switches the server, so the snapshot alone decides.
      const serverHere = !accounts() || serverWorkspace === id;
      if (snapshot.workspaceId === id && pendingSwitches === 0 && serverHere) {
        latestEnter = null;
        return Promise.resolve();
      }
      const promise = enter(id, seq).finally(() => {
        if (latestEnter?.promise === promise) latestEnter = null;
      });
      latestEnter = { id, promise };
      return promise;
    },

    async defaultWorkspace(search) {
      await store.ready();
      if (!accounts()) return principal();
      const ids = await memberIds();
      const fromUrl = new URLSearchParams(search).get('w');
      const stored = storage.getItem(PIN_KEY);
      return [fromUrl, stored].find((c): c is string => !!c && ids.has(c)) ?? (await principal());
    },

    signOut() {
      signingOut ??= (async () => {
        signedOut = true;
        try {
          await api.logout();
        } catch {
          // The cleanup below still runs; the server session may already be gone.
        } finally {
          setCsrfToken(null);
          clearPin(storage);
          forgetWorkspace();
          // Not awaited: the server session is gone and re-exchange is blocked, so
          // leaving never waits on Supabase answering.
          if (accounts() && supabaseLoad) {
            void supabaseLoad.then((auth) => auth.signOut()).catch(() => {});
          }
          update({ status: 'signed-out' });
          signingOut = null;
        }
      })();
      return signingOut;
    },

    markSignedIn() {
      signedOut = false;
      principalWorkspace = null;
      if (snapshot.supabase) setReauthenticator(reauthenticate);
      update({ status: 'signed-in' });
      // The sign-in route exchanged the token itself, so the label is unknown; refetch it.
      void principal().catch(() => {});
    },

    forgetDeletedWorkspace(workspaceId) {
      if (storage.getItem(PIN_KEY) === workspaceId) clearPin(storage);
      principalWorkspace = null;
    },

    setSignInNotice(message) {
      update({ signInNotice: message });
    }
  };
  return store;
}
